import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { Box, Button } from "@mui/material";
import personBuyImg from "../../img/person.png";
import personRentImg from "../../img/person-signing-2.png";

const navy = "#12294B";
const accentBlue = "#1A56A8";
const accentOrange = "#E27C00";

const CheckBullet = () => (
  <Box
    sx={{
      width: "20px",
      height: "20px",
      flexShrink: 0,
      borderRadius: "50%",
      backgroundColor: "#2FA24F",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      mt: "3px",
    }}
  >
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
      <path
        d="M20 6L9 17l-5-5"
        stroke="#fff"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

const PersonIcon = ({ size = 22, color = "#fff" }) => (
  <svg width={size} height={size} viewBox="0 0 640 640" fill={color}>
    <path d="M320 320C390.7 320 448 262.7 448 192C448 121.3 390.7 64 320 64C249.3 64 192 121.3 192 192C192 262.7 249.3 320 320 320zM290.3 368C191.8 368 112 447.8 112 546.3C112 562.7 125.3 576 141.7 576L498.3 576C514.7 576 528 562.7 528 546.3C528 447.8 448.2 368 349.7 368L290.3 368z" />
  </svg>
);

const LockIcon = () => (
  <svg width="12" height="14" viewBox="0 0 12 14" fill="none">
    <path
      d="M6 0C4.34 0 3 1.34 3 3v2H2.5A1.5 1.5 0 0 0 1 6.5v6A1.5 1.5 0 0 0 2.5 14h7a1.5 1.5 0 0 0 1.5-1.5v-6A1.5 1.5 0 0 0 9.5 5H9V3c0-1.66-1.34-3-3-3Zm1.5 5h-3V3a1.5 1.5 0 0 1 3 0v2Z"
      fill="#7A8794"
    />
  </svg>
);

const stepIcons = {
  upload: (
    <svg width="20" height="20" viewBox="0 0 640 640" fill={accentBlue}>
      <path d="M160 240C160 178.1 210.1 128 272 128C311.3 128 345.8 148.2 365.8 178.8C370.2 185.5 378.9 187.9 386.1 184.5C397.5 179 410.4 176 424 176C472.6 176 512 215.4 512 264C512 278.1 508.7 291.4 502.8 303.2C500.8 307.3 500.6 312.1 502.3 316.3C504 320.5 507.5 323.9 511.8 325.4C549.2 338.5 576 374.2 576 416C576 469 533 512 480 512L176 512C114.1 512 64 461.9 64 400C64 346.3 101.8 301.4 152.2 290.5C156.5 289.6 160.3 286.9 162.5 283.1C164.7 279.3 165.4 274.8 164.2 270.5C161.5 260.8 160 250.6 160 240zM272 96C192.5 96 128 160.5 128 240C128 248 128.7 255.9 129.9 263.5C73 282.7 32 336.5 32 400C32 479.5 96.5 544 176 544L480 544C550.7 544 608 486.7 608 416C608 366.1 579.5 323 537.9 301.8C541.8 289.9 544 277.2 544 264C544 197.7 490.3 144 424 144C410.3 144 397.1 146.3 384.8 150.5C358.4 117.3 317.7 96 272 96zM331.3 260.7C325.1 254.5 314.9 254.5 308.7 260.7L236.7 332.7C230.5 338.9 230.5 349.1 236.7 355.3C242.9 361.5 253.1 361.5 259.3 355.3L304 310.6L304 432C304 440.8 311.2 448 320 448C328.8 448 336 440.8 336 432L336 310.6L380.7 355.3C386.9 361.5 397.1 361.5 403.3 355.3C409.5 349.1 409.5 338.9 403.3 332.7L331.3 260.7z" />
    </svg>
  ),
  check: (
    <svg width="20" height="20" viewBox="0 0 640 640" fill={accentBlue}>
      <path d="M320 96L192 96C174.3 96 160 110.3 160 128L160 512C160 529.7 174.3 544 192 544L448 544C465.7 544 480 529.7 480 512L480 256L384 256C348.7 256 320 227.3 320 192L320 96zM466.7 224L352 109.3L352 192C352 209.7 366.3 224 384 224L466.7 224zM128 128C128 92.7 156.7 64 192 64L325.5 64C342.5 64 358.8 70.7 370.8 82.7L493.3 205.3C505.3 217.3 512 233.6 512 250.6L512 512C512 547.3 483.3 576 448 576L192 576C156.7 576 128 547.3 128 512L128 128zM370.7 376C371.6 354.5 360.6 334.2 342.1 323.2C323.6 312.1 300.5 312.1 282 323.2C263.5 334.2 252.5 354.5 253.4 376C252.5 397.5 263.5 417.8 282 428.8C300.5 439.9 323.6 439.9 342.1 428.8C360.6 417.8 371.6 397.5 370.7 376zM361.9 448.5C347.7 458.3 330.5 464 312 464C263.4 464 224 424.6 224 376C224 327.4 263.4 288 312 288C360.6 288 400 327.4 400 376C400 394.5 394.3 411.7 384.5 425.9L427.3 468.7C433.5 474.9 433.5 485.1 427.3 491.3C421.1 497.5 410.9 497.5 404.7 491.3L361.9 448.5z" />
    </svg>
  ),
  results: (
    <svg width="20" height="20" viewBox="0 0 640 640" fill={accentBlue}>
      <path d="M256 96L128 96C110.3 96 96 110.3 96 128L96 512C96 529.7 110.3 544 128 544L289.4 544C294.6 555.3 300.8 566 308 576L128 576C92.7 576 64 547.3 64 512L64 128C64 92.7 92.7 64 128 64L261.5 64C278.5 64 294.8 70.7 306.8 82.7L429.3 205.3C441.3 217.3 448 233.6 448 250.6L448 272.7C437 273.6 426.4 275.4 416 278.1L416 256.1L320 256.1C284.7 256.1 256 227.4 256 192.1L256 96.1zM402.7 224L288 109.3L288 192C288 209.7 302.3 224 320 224L402.7 224zM464 576C525.9 576 576 525.9 576 464C576 402.1 525.9 352 464 352C402.1 352 352 402.1 352 464C352 525.9 402.1 576 464 576zM464 320C543.5 320 608 384.5 608 464C608 543.5 543.5 608 464 608C384.5 608 320 543.5 320 464C320 384.5 384.5 320 464 320zM521.4 403.1C528.5 408.3 530.1 418.3 524.9 425.5L460.9 513.5C458.1 517.3 453.9 519.7 449.2 520C444.5 520.3 439.9 518.7 436.6 515.4L396.6 475.4C390.4 469.2 390.4 459 396.6 452.8C402.8 446.6 413 446.6 419.2 452.8L446 479.6L499 406.7C504.2 399.6 514.2 398 521.4 403.2z" />
    </svg>
  ),
  expert: <PersonIcon size={18} color={accentBlue} />,
};

const whyIcons = {
  understand: (
    <svg width="20" height="20" viewBox="0 0 640 640" fill={accentBlue}>
      <path d="M331.6 68.5L539.1 149L558.3 156.4L559.5 176.9C562.4 226.9 554.6 303.2 522.2 377.8C489.5 453 431.1 527.8 332.8 570.4L320.1 575.9L307.4 570.4C208.9 527.9 150.6 453.2 117.9 377.9C85.5 303.3 77.7 226.9 80.6 177L81.8 156.5L101 149L308.5 68.5L320 64L331.6 68.5zM377.4 233.8L291.8 351.6C272.5 331.6 258.1 316.7 248.6 306.9L214.1 340.2C220.3 346.6 241.3 368.3 277.2 405.4L297 426L313.8 402.9L416.2 262.1L430.3 242.7L391.5 214.5L377.4 233.9z" />
    </svg>
  ),
  spot: (
    <svg width="20" height="20" viewBox="0 0 640 640" fill={accentBlue}>
      <path d="M432.4 272C432.4 183.6 360.8 112 272.4 112C184 112 112.4 183.6 112.4 272C112.4 360.4 184 432 272.4 432C360.8 432 432.4 360.4 432.4 272zM401.6 435.1C366.1 463.2 321.3 480 272.5 480C157.6 480 64.5 386.9 64.5 272C64.5 157.1 157.6 64 272.5 64C387.4 64 480.5 157.1 480.5 272C480.5 320.8 463.7 365.7 435.6 401.1L569.5 535L586.5 552L552.6 585.9L535.6 568.9L401.7 435z" />
    </svg>
  ),
  avoid: (
    <svg width="20" height="20" viewBox="0 0 640 640" fill={accentBlue}>
      <path d="M128 96C92.7 96 64 124.7 64 160L64 448C64 483.3 92.7 512 128 512L512 512C547.3 512 576 483.3 576 448L576 256C576 220.7 547.3 192 512 192L136 192C122.7 192 112 181.3 112 168C112 154.7 122.7 144 136 144L520 144C533.3 144 544 133.3 544 120C544 106.7 533.3 96 520 96L128 96zM480 320C497.7 320 512 334.3 512 352C512 369.7 497.7 384 480 384C462.3 384 448 369.7 448 352C448 334.3 462.3 320 480 320z" />
    </svg>
  ),
  ask: (
    <svg width="20" height="20" viewBox="0 0 640 640" fill={accentBlue}>
      <path d="M267.7 576.9L267.7 576.9L359.6 512L480 512C533 512 576 469 576 416L576 192C576 139 533 96 480 96L160 96C107 96 64 139 64 192L64 416C64 469 107 512 160 512L192 512L192 584C192 593 197 601.2 205 605.3C213 609.4 222.6 608.8 229.9 603.6L267.7 576.9zM359.6 464C349.7 464 340 467.1 331.9 472.8C303.3 493 272.6 514.6 240 537.7L240 488C240 485.2 239.5 482.4 238.6 479.9C235.3 470.6 226.4 464 216 464L160 464C133.5 464 112 442.5 112 416L112 192C112 165.5 133.5 144 160 144L480 144C506.5 144 528 165.5 528 192L528 416C528 442.5 506.5 464 480 464L359.6 464zM184 272C170.7 272 160 282.7 160 296C160 309.3 170.7 320 184 320L312 320C325.3 320 336 309.3 336 296C336 282.7 325.3 272 312 272L184 272zM408 272C394.7 272 384 282.7 384 296C384 309.3 394.7 320 408 320L456 320C469.3 320 480 309.3 480 296C480 282.7 469.3 272 456 272L408 272zM184 368C170.7 368 160 378.7 160 392C160 405.3 170.7 416 184 416L232 416C245.3 416 256 405.3 256 392C256 378.7 245.3 368 232 368L184 368zM328 368C314.7 368 304 378.7 304 392C304 405.3 314.7 416 328 416L456 416C469.3 416 480 405.3 480 392C480 378.7 469.3 368 456 368L328 368z" />
    </svg>
  ),
};

// Per-module configuration: translation namespace, hero image and bullet keys
const MODULE_CONFIG = {
  PURCHASE: {
    prefix: "maatwerkIntroPurchase",
    image: personBuyImg,
    heroBullets: ["conditions", "transfer", "questions"],
  },
  RENTAL: {
    prefix: "maatwerkIntroRental",
    image: personRentImg,
    imageOverlay:
      "linear-gradient(89.91deg, rgba(67, 155, 217, 0) 31.15%, rgba(67, 155, 217, 0.55) 86.46%)",
    heroBullets: ["increases", "maintenance", "notice"],
  },
};

// Icons for the "who checks it" pricing row, per plan (outline style).
const whoIconProps = {
  width: "18",
  height: "18",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: navy,
  strokeWidth: "1.8",
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const whoIcons = {
  standard: (
    <svg {...whoIconProps}>
      <rect x="5" y="5" width="14" height="14" rx="3" />
      <rect x="9.5" y="9.5" width="5" height="5" rx="1" />
      <path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" />
    </svg>
  ),
  premium: (
    <svg {...whoIconProps}>
      <circle cx="9.5" cy="7" r="3.5" />
      <path d="M3 20c.6-3.6 3.2-5.6 6.5-5.6 1.2 0 2.3.3 3.2.8" />
      <path d="M15.5 18.5l2 2 3.5-4.5" />
    </svg>
  ),
  premiumPlus: (
    <svg {...whoIconProps}>
      <path d="M3.5 11L12 3.5 20.5 11" />
      <path d="M5.5 9.5V20.5H18.5V9.5" />
    </svg>
  ),
};

const ContractIntro = ({
  module,
  onStart,
  showPricing = false,
  selectedPlan,
  onChoosePlan,
}) => {
  const { t } = useTranslation();
  const { prefix: p, image, imageOverlay, heroBullets } = MODULE_CONFIG[module];

  const stepKeys = ["upload", "check", "results"];
  const statKeys = ["contracts", "review"];
  const whyKeys = ["understand", "spot", "avoid", "ask"];
  const planKeys = ["standard", "premium", "premiumPlus"];
  const rowKeys = ["bestWhen", "receive", "who", "turnaround"];

  const chipSx = {
    display: "inline-block",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "1px",
    textTransform: "uppercase",
    borderRadius: "999px",
    padding: "4px 12px",
    whiteSpace: "nowrap",
  };

  const recommendedChip = (
    <Box
      component="span"
      sx={{ ...chipSx, backgroundColor: accentOrange, color: "#fff" }}
    >
      {t(`${p}.pricing.recommended`)}
    </Box>
  );

  const yourChoiceChip = (
    <Box component="span" sx={{ ...chipSx, backgroundColor: navy, color: "#fff" }}>
      {t(`${p}.pricing.yourChoice`)}
    </Box>
  );

  const cellBorder = "1px solid #EAF0F6";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "28px",
        padding: { mobile: "20px 16px 48px", ipad: "24px 12px 56px", laptop: "24px 0 64px" },
      }}
    >
      {/* Page title */}
      <Typography
        sx={{ fontSize: "20px", fontWeight: 800, color: navy, lineHeight: 1 }}
      >
        {t(`${p}.pageTitle`)}
      </Typography>

      {/* Hero */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "20px",
          overflow: "hidden",
          display: "flex",
          flexDirection: { mobile: "column-reverse", laptop: "row" },
          boxShadow: "0 2px 14px rgba(18, 41, 75, 0.08)",
        }}
      >
        <Box
          sx={{
            flex: 1.4,
            padding: showPricing
              ? { mobile: "28px 22px 20px", ipad: "40px 44px 28px" }
              : { mobile: "28px 22px", ipad: "40px 44px" },
            display: "flex",
            flexDirection: "column",
            gap: "18px",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 800,
              letterSpacing: "2px",
              textTransform: "uppercase",
              color: accentOrange,
            }}
          >
            {t(`${p}.eyebrow`)}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontSize: { mobile: "32px", ipad: "44px" },
              fontWeight: 800,
              color: navy,
              lineHeight: 1.15,
            }}
          >
            {t(`${p}.heroTitle`)}
          </Typography>
          <Typography
            sx={{
              fontSize: "16px",
              lineHeight: 1.7,
              color: "#44546A",
              maxWidth: "560px",
            }}
          >
            {t(`${p}.heroText`)}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: showPricing ? "16px" : "10px",
            }}
          >
            {heroBullets.map((key) => (
              <Box key={key} sx={{ display: "flex", gap: "12px" }}>
                <CheckBullet />
                <Typography
                  sx={{ fontSize: "15px", color: "#33445C", fontWeight: 500 }}
                >
                  {t(`${p}.heroBullets.${key}`)}
                </Typography>
              </Box>
            ))}
          </Box>
          {!showPricing && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "18px",
                marginTop: "10px",
              }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#FF8C00",
                  color: "#fff",
                  borderRadius: "999px",
                  textTransform: "none",
                  fontSize: "17px",
                  fontWeight: 700,
                  padding: "10px 32px",
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#E67E00" },
                }}
                onClick={onStart}
              >
                {t(`${p}.start`)}
                <span style={{ marginLeft: "10px" }}>→</span>
              </Button>
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <LockIcon />
                <Typography
                  sx={{ fontSize: "13px", color: "#7A8794", fontWeight: 500 }}
                >
                  {t(`${p}.trust`)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            // The image fills whatever height the text column sets, instead
            // of its own intrinsic height stretching the hero.
            height: { mobile: "260px", laptop: "auto" },
            position: "relative",
            overflow: "hidden",
            "&::after": imageOverlay
              ? {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background: imageOverlay,
                  pointerEvents: "none",
                }
              : undefined,
          }}
        >
          <Box
            component="img"
            src={image}
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </Box>
      </Box>

      {/* How it works + expert / why cards */}
      {!showPricing && (
      <Box
        sx={{
          display: "flex",
          flexDirection: { mobile: "column", laptop: "row" },
          gap: { mobile: "28px", laptop: "40px" },
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ flex: 1.25, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 800,
              color: navy,
              marginBottom: "32px",
            }}
          >
            {t(`${p}.howTitle`)}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {stepKeys.map((key, index) => (
              <Box key={key} sx={{ display: "flex", gap: "18px" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: "44px",
                      height: "44px",
                      flexShrink: 0,
                      borderRadius: "50%",
                      backgroundColor: "#EAF4FB",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {stepIcons[key]}
                  </Box>
                  {index < stepKeys.length - 1 && (
                    <Box
                      sx={{
                        width: "2px",
                        flex: 1,
                        minHeight: "26px",
                        backgroundColor: "#D6E6F5",
                        my: "8px",
                      }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    pb: index < stepKeys.length - 1 ? "32px" : 0,
                    pt: "4px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "10px",
                      mb: "6px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: navy,
                        lineHeight: 1.3,
                      }}
                    >
                      {index + 1} · {t(`${p}.steps.${key}.title`)}
                    </Typography>
                    {key === "expert" && (
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: "#FCE4BC",
                          color: "#9A5B00",
                          fontSize: "10px",
                          fontWeight: 800,
                          letterSpacing: "0.8px",
                          textTransform: "uppercase",
                          borderRadius: "999px",
                          padding: "2px 9px",
                        }}
                      >
                        {t(`${p}.optional`)}
                      </Box>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14.5px",
                      lineHeight: 1.6,
                      color: "#55657C",
                    }}
                  >
                    {t(`${p}.steps.${key}.text`)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          {/* Expert card */}
          <Box
            sx={{
              backgroundColor: navy,
              borderRadius: "16px",
              padding: "26px 28px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: "#fff",
                lineHeight: 1.3,
              }}
            >
              {t(`${p}.expertCard.title`)}
            </Typography>
            <Typography
              sx={{
                fontSize: "14.5px",
                lineHeight: 1.65,
                color: "rgba(255,255,255,0.85)",
              }}
            >
              {t(`${p}.expertCard.text`)}
            </Typography>
            <Box sx={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
              {statKeys.map((key) => (
                <Box key={key} sx={{ minWidth: "90px" }}>
                  <Typography
                    sx={{
                      fontSize: "21px",
                      fontWeight: 800,
                      color: "#FFA726",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t(`${p}.expertCard.stats.${key}.value`)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "rgba(255,255,255,0.65)",
                    }}
                  >
                    {t(`${p}.expertCard.stats.${key}.label`)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Why buyers/tenants check first */}
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "24px 28px",
              boxShadow: "0 2px 10px rgba(18, 41, 75, 0.06)",
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: navy,
                marginBottom: "18px",
              }}
            >
              {t(`${p}.whyTitle`)}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { mobile: "1fr", ipad: "1fr 1fr" },
                gap: "16px 24px",
              }}
            >
              {whyKeys.map((key) => (
                <Box
                  key={key}
                  sx={{ display: "flex", gap: "10px", alignItems: "flex-start" }}
                >
                  <Box sx={{ flexShrink: 0, mt: "1px" }}>{whyIcons[key]}</Box>
                  <Typography
                    sx={{
                      fontSize: "13.5px",
                      lineHeight: 1.45,
                      color: "#44546A",
                      fontWeight: 500,
                    }}
                  >
                    {t(`${p}.why.${key}`)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      )}

      {/* Pricing: pick a tier, then upload on the next page */}
      {showPricing && (
        <Box>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 800,
              letterSpacing: "2px",
              textTransform: "uppercase",
              color: accentOrange,
              marginBottom: "6px",
            }}
          >
            {t(`${p}.pricing.eyebrow`)}
          </Typography>
          <Typography
            sx={{
              fontSize: "26px",
              fontWeight: 800,
              color: navy,
              lineHeight: 1.2,
            }}
          >
            {t(`${p}.pricing.title`)}
          </Typography>
          <Typography
            sx={{
              fontSize: "15px",
              lineHeight: 1.6,
              color: "#55657C",
              maxWidth: "640px",
              marginTop: "8px",
              marginBottom: "24px",
            }}
          >
            {t(`${p}.pricing.note`)}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { mobile: "1fr", laptop: "repeat(3, 1fr)" },
              gap: "20px",
              alignItems: "stretch",
            }}
          >
            {planKeys.map((planKey) => {
              const isSelected = planKey === selectedPlan;
              return (
                <Box
                  key={planKey}
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "24px",
                    border: `2px solid ${isSelected ? navy : "transparent"}`,
                    boxShadow: "0 2px 14px rgba(18, 41, 75, 0.07)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      padding: "24px 26px 20px",
                      borderBottom: cellBorder,
                      backgroundColor: isSelected ? "#DCE7F3" : "transparent",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: "12px",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "24px", fontWeight: 700, color: navy }}
                      >
                        {t(`${p}.pricing.${planKey}.name`)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "28px",
                          fontWeight: 700,
                          color: navy,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t(`${p}.pricing.${planKey}.price`)}
                      </Typography>
                    </Box>
                    {(isSelected || planKey === "premium") && (
                      <Box sx={{ marginTop: "8px" }}>
                        {isSelected ? yourChoiceChip : recommendedChip}
                      </Box>
                    )}
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      padding: "6px 26px 26px",
                    }}
                  >
                    <Box sx={{ flex: 1, marginBottom: "16px" }}>
                      {rowKeys.map((rowKey, index) => (
                        <Box
                          key={rowKey}
                          sx={{
                            padding: "16px 0",
                            borderTop: index === 0 ? "none" : cellBorder,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 700,
                              letterSpacing: "1.4px",
                              textTransform: "uppercase",
                              color: "#5A6B85",
                              marginBottom: "6px",
                            }}
                          >
                            {t(`${p}.pricing.rows.${rowKey}`)}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "10px",
                            }}
                          >
                            {rowKey === "who" && (
                              <Box sx={{ flexShrink: 0, marginTop: "3px" }}>
                                {whoIcons[planKey]}
                              </Box>
                            )}
                            <Typography
                              sx={{
                                fontSize: "16px",
                                fontWeight: 500,
                                lineHeight: 1.6,
                                color: "#2A3C55",
                              }}
                            >
                              {t(`${p}.pricing.${planKey}.${rowKey}`)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    <Button
                      fullWidth
                      disableElevation
                      variant={
                        isSelected || planKey === "premiumPlus"
                          ? "contained"
                          : "outlined"
                      }
                      sx={{
                        borderRadius: "999px",
                        textTransform: "none",
                        fontSize: "16px",
                        fontWeight: 700,
                        padding: "13px 22px",
                        ...(isSelected
                          ? {
                              backgroundColor: "#FF8C00",
                              color: "#fff",
                              "&:hover": { backgroundColor: "#E67E00" },
                            }
                          : planKey === "premiumPlus"
                          ? {
                              backgroundColor: navy,
                              color: "#fff",
                              "&:hover": { backgroundColor: "#0B1C36" },
                            }
                          : {
                              backgroundColor: "#fff",
                              borderColor: navy,
                              borderWidth: "1.5px",
                              color: navy,
                              "&:hover": {
                                borderColor: navy,
                                backgroundColor: "#F4F8FC",
                              },
                            }),
                      }}
                      onClick={() => onChoosePlan(planKey)}
                    >
                      {t(`${p}.pricing.${planKey}.cta`)}
                      {isSelected && (
                        <span style={{ marginLeft: "8px" }}>→</span>
                      )}
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Bottom CTA: mobile/tablet only, the hero button covers desktop */}
      {!showPricing && (
        <Box
          sx={{
            display: { mobile: "flex", laptop: "none" },
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#FF8C00",
              color: "#fff",
              borderRadius: "999px",
              textTransform: "none",
              fontSize: "17px",
              fontWeight: 700,
              padding: "10px 40px",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#E67E00" },
            }}
            onClick={onStart}
          >
            {t(`${p}.start`)}
            <span style={{ marginLeft: "10px" }}>→</span>
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <LockIcon />
            <Typography
              sx={{ fontSize: "13px", color: "#7A8794", fontWeight: 500 }}
            >
              {t(`${p}.trust`)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const MaatwerkIntro = ({ module, basePath, showPricing = false }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const isPurchase = module === "PURCHASE";
  // Route prefix for this flow; overridable so other routers (e.g. the demo
  // subdomain) can mount the same pages under their own paths.
  const base = basePath ?? (isPurchase ? "" : "/rental");

  // A plan carried in the URL (e.g. when coming back from the upload page)
  // marks that card as the visitor's current choice; Standard by default.
  const planParam = new URLSearchParams(search).get("plan");
  const selectedPlan = ["standard", "premium", "premiumPlus"].includes(planParam)
    ? planParam
    : "standard";

  function goToUpload(planKey) {
    const params = new URLSearchParams(search);
    if (planKey) params.set("plan", planKey);
    const query = params.toString();
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(`${base}/upload${query ? `?${query}` : ""}`);
  }

  return (
    <ContractIntro
      module={module}
      onStart={() => goToUpload(null)}
      showPricing={showPricing}
      selectedPlan={selectedPlan}
      onChoosePlan={goToUpload}
    />
  );
};

export default MaatwerkIntro;
